/* iter55k — Phase 5: Editor Review request dialog.
 *
 * Flow:
 *   1. Fetch quota + list of user's editor-review history on open.
 *   2. Show history at top (in_progress/completed cards clickable to open viewer).
 *   3. If this month's slot is unused, offer a request form (project + notes).
 *   4. Otherwise show the QuotaExhaustedState.
 *
 * The full review viewer (summary letter + inline comments) is
 * `EditorReviewViewer.jsx` — this dialog only handles the REQUEST flow.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { humanServicesApi } from '@/lib/api';
import { toast } from 'sonner';
import { ScrollText, Loader2, CheckCircle2, Eye } from 'lucide-react';
import { usePW } from '@/lib/store';
import { QuotaExhaustedState } from './CoachingBookDialog';

const STATUS_LABEL = {
  requested:   { text: 'Requested',   color: 'bg-slate-100 text-slate-700' },
  in_progress: { text: 'In progress', color: 'bg-amber-100 text-amber-800' },
  completed:   { text: 'Completed',   color: 'bg-emerald-100 text-emerald-800' },
  cancelled:   { text: 'Cancelled',   color: 'bg-rose-100 text-rose-800' },
};

export function EditorReviewRequestDialog({ open, onOpenChange, onOpenReview }) {
  const { project } = usePW();
  const [loading, setLoading] = useState(true);
  const [quota, setQuota]     = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes]     = useState('');
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, m] = await Promise.all([
        humanServicesApi.quota(),
        humanServicesApi.reviewMine(),
      ]);
      setQuota(q);
      setHistory(m.reviews || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'upgrade_required') {
        toast.info('Available on Premier Studio', {
          description: 'Upgrade in Settings → Subscription to request an editor review.',
        });
        onOpenChange(false);
      } else {
        toast.error('Failed to load editor review status.');
      }
    } finally {
      setLoading(false);
    }
  }, [onOpenChange]);

  useEffect(() => {
    if (open) {
      setNotes('');
      load();
    }
  }, [open, load]);

  const submit = async () => {
    if (!project?.id) {
      toast.error('No project loaded — open a project first.');
      return;
    }
    setBusy(true);
    try {
      await humanServicesApi.reviewRequest(project.id, notes);
      toast.success('Editor review requested.');
      // Refresh so the new row appears in history + quota is updated.
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Request failed.');
    } finally {
      setBusy(false);
    }
  };

  const alreadyUsed = quota?.editor_review?.used >= (quota?.editor_review?.limit || 0)
                     && (quota?.editor_review?.limit || 0) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="editor-review-request-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2 text-[14px]">
            <ScrollText className="h-4 w-4 text-primary" /> Request an editor review
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Premier Studio includes one monthly manuscript pass — the editor
            reads your current project, drops inline paragraph comments, and
            writes a summary letter. All viewable inside the app.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-[12px] text-muted-foreground">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            <div className="mt-2">Loading…</div>
          </div>
        ) : (
          <>
            {history.length > 0 && (
              <section className="mb-3">
                <div className="mb-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Your reviews
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1"
                     data-testid="editor-review-history-list">
                  {history.slice(0, 6).map((r) => {
                    const label = STATUS_LABEL[r.status] || STATUS_LABEL.requested;
                    const clickable = r.status === 'completed' || r.status === 'in_progress';
                    return (
                      <button
                        key={r.id}
                        onClick={() => clickable && onOpenReview?.(r.id)}
                        disabled={!clickable}
                        data-testid={`editor-review-row-${r.id}`}
                        className={`flex w-full items-center justify-between rounded-md border border-border px-3 py-1.5 text-left text-[12px] ${
                          clickable ? 'hover:bg-secondary' : 'opacity-70'
                        }`}
                      >
                        <span className="min-w-0 truncate">
                          <span className="text-muted-foreground">{r.month_key}</span>
                          {' · '}
                          <span className="font-medium">{r.project_name || 'Untitled'}</span>
                        </span>
                        <span className="ml-2 shrink-0 inline-flex items-center gap-1.5">
                          <span className={`rounded px-1.5 py-0.5 text-[9.5px] font-medium ${label.color}`}>
                            {label.text}
                          </span>
                          {clickable && <Eye className="h-3 w-3 text-muted-foreground" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {alreadyUsed ? (
              <QuotaExhaustedState monthKey={quota.month_key}
                                     kind="editor_review"
                                     onClose={() => onOpenChange(false)} />
            ) : (
              <>
                <div className="rounded-md border border-border bg-secondary/40 p-3 text-[11.5px]">
                  Submitting for:{' '}
                  <span className="font-semibold" data-testid="editor-review-project-name">
                    {project?.title || project?.name || 'Untitled project'}
                  </span>
                </div>
                <div className="mt-3">
                  <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
                    Anything the editor should focus on? (optional)
                  </label>
                  <Textarea
                    data-testid="editor-review-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g. Line-edit the first three chapters, then structural notes on the rest."
                    className="h-24 text-[12px]"
                    maxLength={4000}
                  />
                </div>
                <DialogFooter>
                  <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
                          data-testid="editor-review-cancel-btn">
                    Cancel
                  </Button>
                  <Button size="sm"
                          onClick={submit}
                          disabled={busy || !project?.id}
                          data-testid="editor-review-submit-btn">
                    {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <CheckCircle2 className="mr-1 h-3 w-3" />}
                    Submit request
                  </Button>
                </DialogFooter>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
